export type ChatMessage = {
  role: "system" | "user" | "assistant";
  content: string;
};

export type ChatOptions = {
  num_predict?: number;
  num_ctx?: number;
  temperature?: number;
};

export type ChatDoneInfo = {
  done_reason: string;
  truncated: boolean;
  prompt_eval_count: number;
  eval_count: number;
  num_ctx: number;
  num_predict: number;
  context_used: number;
  context_remaining: number;
};

async function json<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error(
      (data as { detail?: string; error?: string }).detail ||
        (data as { error?: string }).error ||
        `HTTP ${res.status}`
    );
  }
  return data;
}

export const api = {
  async me() {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (res.status === 401) return { authenticated: false as const };
    return json<{ authenticated: boolean; sub?: string }>(res);
  },

  async login(body: { password?: string; invite?: string }) {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return json<{ ok: boolean; sub: string }>(res);
  },

  async logout() {
    const res = await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
    return json<{ ok: boolean }>(res);
  },

  async health() {
    const res = await fetch("/api/llm/health", { credentials: "include" });
    return json<{ ok: boolean; detail?: string }>(res);
  },

  async config() {
    const res = await fetch("/api/llm/config", { credentials: "include" });
    return json<{ num_predict: number; num_ctx: number; default_model: string }>(res);
  },

  async models() {
    const res = await fetch("/api/llm/models", { credentials: "include" });
    return json<{ models: { name: string; size?: number }[] }>(res);
  },

  async chatStream(
    body: {
      model: string;
      messages: ChatMessage[];
      think?: boolean;
      options?: ChatOptions;
    },
    handlers: {
      onDelta: (d: { content: string; thinking: string }) => void;
      onDone: (info: ChatDoneInfo) => void;
      onError: (msg: string) => void;
      signal?: AbortSignal;
    }
  ) {
    const res = await fetch("/api/llm/chat", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: handlers.signal,
    });

    if (!res.ok || !res.body) {
      let detail = `HTTP ${res.status}`;
      try {
        const j = (await res.json()) as { error?: string; detail?: string };
        detail = j.detail || j.error || detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let finished = false;
    let streamErr: string | null = null;

    const finish = (info?: Partial<ChatDoneInfo>) => {
      if (finished) return;
      finished = true;
      const num_ctx = info?.num_ctx ?? 0;
      const used = info?.context_used ?? (info?.prompt_eval_count || 0) + (info?.eval_count || 0);
      handlers.onDone({
        done_reason: info?.done_reason || "stop",
        truncated: Boolean(info?.truncated),
        prompt_eval_count: info?.prompt_eval_count ?? 0,
        eval_count: info?.eval_count ?? 0,
        num_ctx,
        num_predict: info?.num_predict ?? 0,
        context_used: used,
        context_remaining: info?.context_remaining ?? Math.max(0, num_ctx - used),
      });
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop() || "";
      for (const part of parts) {
        const lines = part.split("\n");
        let event = "message";
        let data = "";
        for (const line of lines) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          if (line.startsWith("data:")) data += line.slice(5).trim();
        }
        if (!data) continue;
        try {
          const payload = JSON.parse(data) as Partial<ChatDoneInfo> & {
            content?: string;
            thinking?: string;
            error?: string;
          };
          if (event === "delta") {
            handlers.onDelta({
              content: payload.content || "",
              thinking: payload.thinking || "",
            });
          } else if (event === "error") {
            streamErr = payload.error || "stream error";
            handlers.onError(streamErr);
          } else if (event === "done") {
            finish(payload);
          }
        } catch {
          /* ignore */
        }
      }
    }
    finish();
    if (streamErr) throw new Error(streamErr);
  },
};
